"use client";

import { useState, useEffect, useMemo } from "react";
import { useDebounce } from "@/hooks/useDebounce";
import { motion, AnimatePresence } from "framer-motion";

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 100, damping: 15 },
    },
};

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import {
    ArrowLeft, Package, Funnel, X, Trophy, CurrencyDollar, ChartBar,
} from "@phosphor-icons/react";
import { CaretDown } from "@phosphor-icons/react";
import { Loader2, RefreshCcw, ShieldAlert, Download, X as XIcon } from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format, subDays, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { Link } from "react-router-dom";
import axios from "axios";
import { numberWithCommas } from "@/lib/formatCommas";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useSelector } from "react-redux";

const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1/';

const CHART_COLORS = ['#FFD700', '#A0A0A0', '#CD7F32', '#0052CC', '#36B37E', '#6554C0', '#00B8D9', '#FF8B00', '#FF5630', '#FFAB00'];

const MetricCard = ({ title, value, subtitle, icon: Icon, color = "blue", index = 0 }) => {
    const colorClasses = {
        blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
        green: "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400",
        orange: "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400",
        purple: "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400",
    };

    return (
        <motion.div
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            whileHover={{
                scale: 1.03, y: -4,
                transition: { type: "spring", stiffness: 400, damping: 17 }
            }}
            custom={index}
        >
            <Card className="bg-white dark:bg-slate-900 border shadow-sm hover:shadow-lg transition-shadow duration-300 h-full">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{title}</p>
                            <motion.p
                                className="text-2xl font-bold mt-1"
                                initial={{ opacity: 0, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                            >
                                {value}
                            </motion.p>
                            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
                        </div>
                        <motion.div
                            className={cn("p-2 rounded-lg", colorClasses[color])}
                            whileHover={{ rotate: [0, -10, 10, -5, 0] }}
                            transition={{ duration: 0.5 }}
                        >
                            <Icon className="h-5 w-5" weight="duotone" />
                        </motion.div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

export default function TopProductsReportPage() {
    const { user } = useSelector((state) => state.auth);
    const [isLoading, setIsLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const [sortBy, setSortBy] = useState("qty");

    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 400);

    const [dateRange, setDateRange] = useState("this_month");
    const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
    const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [shopId, setShopId] = useState("");
    const [shops, setShops] = useState([]);

    const hasPermission = () => {
        if (!user?.role?.permissions) return false;
        const permissions = typeof user.role.permissions === 'string'
            ? JSON.parse(user.role.permissions)
            : user.role.permissions;
        return permissions.includes('ViewReports') || permissions.includes('Settings');
    };

    useEffect(() => {
        const fetchShops = async () => {
            try {
                const res = await axios.get(`${baseUrl}shops`, { withCredentials: true });
                if (res.data.success) setShops(res.data.data || []);
            } catch (e) { console.error("Failed to fetch shops"); }
        };
        fetchShops();
    }, []);

    useEffect(() => {
        const now = new Date();
        switch (dateRange) {
            case "today":
                setStartDate(format(now, "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_7_days":
                setStartDate(format(subDays(now, 7), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_30_days":
                setStartDate(format(subDays(now, 30), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "this_month":
                setStartDate(format(startOfMonth(now), "yyyy-MM-dd"));
                setEndDate(format(now, "yyyy-MM-dd"));
                break;
            case "last_month": {
                const lastMonth = subMonths(now, 1);
                setStartDate(format(startOfMonth(lastMonth), "yyyy-MM-dd"));
                setEndDate(format(endOfMonth(lastMonth), "yyyy-MM-dd"));
                break;
            }
            case "custom":
                break;
        }
    }, [dateRange]);

    const fetchData = async (currentPage) => {
        setIsLoading(true);
        try {
            const params = { startDate, endDate, sortBy, limit, page: currentPage };
            if (shopId) params.shopId = shopId;
            if (debouncedSearch) params.search = debouncedSearch;

            const res = await axios.get(`${baseUrl}analytics/top-products`, {
                params,
                withCredentials: true,
            });

            if (res.data.success) {
                setProducts(res.data.data || []);
                setSummary(res.data.summary || null);
                setPagination(res.data.pagination || null);
            }
        } catch (error) {
            console.error("Failed to fetch top products:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Reset to page 1 when filters change
    useEffect(() => {
        setPage(1);
        fetchData(1);
    }, [startDate, endDate, shopId, sortBy, limit, debouncedSearch]);

    // Fetch when page changes (but not when filters reset it — handled above)
    useEffect(() => {
        if (page !== 1) fetchData(page);
    }, [page]);

    const currency = user?.shop?.currency || "GHS";

    // Fetch all products (unpaginated) for export
    const fetchAllForExport = async () => {
        try {
            const params = { startDate, endDate, sortBy, limit: 99999, page: 1 };
            if (shopId) params.shopId = shopId;
            if (debouncedSearch) params.search = debouncedSearch;
            const res = await axios.get(`${baseUrl}analytics/top-products`, {
                params, withCredentials: true,
            });
            return res.data.success ? res.data.data : products;
        } catch {
            return products; // fallback to current page
        }
    };

    const buildExportRows = (data) =>
        data.map((p) => ({
            Rank: p.rank,
            "Product Name": p.productName,
            Barcode: p.barcode || "",
            "Qty Sold": p.totalQtySold,
            [`Revenue (${currency})`]: p.totalRevenue.toFixed(2),
            [`Profit (${currency})`]: p.totalProfit.toFixed(2),
            "Margin %": p.totalQtySold > 0 ? `${p.profitMargin}%` : "—",
        }));

    const exportCSV = async () => {
        const data = await fetchAllForExport();
        const rows = buildExportRows(data);
        const headers = Object.keys(rows[0] || {});
        const csvLines = [
            headers.join(","),
            ...rows.map((r) =>
                headers.map((h) => {
                    const val = String(r[h] ?? "");
                    return val.includes(",") ? `"${val}"` : val;
                }).join(",")
            ),
        ];
        const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8" });
        saveAs(blob, `top-products_${startDate}_${endDate}.csv`);
    };

    const exportExcel = async () => {
        const data = await fetchAllForExport();
        const rows = buildExportRows(data);
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Top Products");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buf], { type: "application/octet-stream" }), `top-products_${startDate}_${endDate}.xlsx`);
    };

    const exportPDF = async () => {
        const data = await fetchAllForExport();
        const doc = new jsPDF();

        doc.setFontSize(16);
        doc.text("Top Sold Products Report", 14, 18);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Period: ${format(new Date(startDate), "dd MMM yyyy")} – ${format(new Date(endDate), "dd MMM yyyy")}`, 14, 26);
        doc.text(`Generated: ${format(new Date(), "dd MMM yyyy HH:mm")}`, 14, 32);

        autoTable(doc, {
            startY: 38,
            head: [["#", "Product Name", "Qty Sold", `Revenue (${currency})`, `Profit (${currency})`, "Margin"]],
            body: data.map((p) => [
                p.rank,
                p.productName,
                p.totalQtySold,
                p.totalRevenue.toFixed(2),
                p.totalProfit.toFixed(2),
                p.totalQtySold > 0 ? `${p.profitMargin}%` : "—",
            ]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [48, 35, 45] },
        });

        doc.save(`top-products_${startDate}_${endDate}.pdf`);
    };

    const chartData = useMemo(() => {
        return products.slice(0, 10).map(p => ({
            name: p.productName.length > 20
                ? p.productName.substring(0, 20) + '...'
                : p.productName,
            qty: p.totalQtySold,
            revenue: p.totalRevenue,
        }));
    }, [products]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div className="bg-white dark:bg-slate-800 border rounded-lg shadow-lg p-3 text-sm">
                <p className="font-medium mb-1">{label}</p>
                {payload.map((entry, i) => (
                    <p key={i} style={{ color: entry.color }}>
                        {entry.name === 'qty' ? 'Qty Sold' : 'Revenue'}:{' '}
                        {entry.name === 'qty'
                            ? numberWithCommas(entry.value)
                            : `${currency} ${numberWithCommas(entry.value.toFixed(2))}`}
                    </p>
                ))}
            </div>
        );
    };

    return (
        <div className="mx-auto p-4 space-y-6">
            {!hasPermission() ? (
                <Card className="mx-auto max-w-md mt-20">
                    <CardContent className="pt-6 text-center">
                        <ShieldAlert className="h-12 w-12 mx-auto text-red-500 mb-4" />
                        <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                        <p className="text-muted-foreground mb-4">You don't have permission to view reports.</p>
                        <Link to="/dashboard">
                            <Button>Go to Dashboard</Button>
                        </Link>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Header */}
                    <motion.div
                        className="flex flex-wrap justify-between items-center gap-4"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                    >
                        <div className="flex items-center gap-4">
                            <Link to="/dashboard/reports">
                                <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                                    <Button variant="outline" size="icon"><ArrowLeft className="h-4 w-4" /></Button>
                                </motion.div>
                            </Link>
                            <div>
                                <h1 className="text-2xl font-bold">Top Sold Products</h1>
                                <p className="text-sm text-muted-foreground">Product performance ranked by sales</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" disabled={isLoading || products.length === 0}>
                                        <Download className="h-4 w-4 mr-2" />
                                        Export
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={exportCSV}>Export as CSV</DropdownMenuItem>
                                    <DropdownMenuItem onClick={exportExcel}>Export as Excel</DropdownMenuItem>
                                    <DropdownMenuItem onClick={exportPDF}>Export as PDF</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <Button variant="outline" onClick={() => fetchData(page)} disabled={isLoading}>
                                <RefreshCcw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                                Refresh
                            </Button>
                        </div>
                    </motion.div>

                    {/* Filters */}
                    <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                        <Card className="border">
                            <CollapsibleTrigger asChild>
                                <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50">
                                    <div className="flex items-center gap-2">
                                        <Funnel className="h-4 w-4" />
                                        <span className="font-medium">Filters</span>
                                        <Badge variant="outline" className="ml-2">
                                            {format(new Date(startDate), "MMM dd")} - {format(new Date(endDate), "MMM dd, yyyy")}
                                        </Badge>
                                    </div>
                                    {filtersOpen ? <X className="h-4 w-4" /> : <CaretDown className="h-4 w-4" />}
                                </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="p-4 pt-0 border-t">
                                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                                        <div className="space-y-2">
                                            <Label>Search Product</Label>
                                            <div className="relative">
                                                <Input
                                                    placeholder="Search by product name..."
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    className="pr-8"
                                                />
                                                {search && (
                                                    <button
                                                        onClick={() => setSearch("")}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                    >
                                                        <XIcon className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Date Range</Label>
                                            <Select value={dateRange} onValueChange={setDateRange}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="today">Today</SelectItem>
                                                    <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                                                    <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                                                    <SelectItem value="this_month">This Month</SelectItem>
                                                    <SelectItem value="last_month">Last Month</SelectItem>
                                                    <SelectItem value="custom">Custom Range</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        {dateRange === "custom" && (
                                            <>
                                                <div className="space-y-2">
                                                    <Label>Start Date</Label>
                                                    <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>End Date</Label>
                                                    <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                                </div>
                                            </>
                                        )}

                                        <div className="space-y-2">
                                            <Label>Shop</Label>
                                            <Select value={shopId} onValueChange={(val) => setShopId(val === "all" ? "" : val)}>
                                                <SelectTrigger><SelectValue placeholder="All Shops" /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="all">All Shops</SelectItem>
                                                    {shops.map(shop => (
                                                        <SelectItem key={shop.id} value={shop.id.toString()}>{shop.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Sort By</Label>
                                            <Select value={sortBy} onValueChange={setSortBy}>
                                                <SelectTrigger><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="qty">Quantity Sold</SelectItem>
                                                    <SelectItem value="revenue">Revenue</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            </CollapsibleContent>
                        </Card>
                    </Collapsible>

                    <AnimatePresence mode="wait">
                        {isLoading ? (
                            <motion.div
                                className="flex items-center justify-center h-64"
                                key="loader"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                >
                                    <Loader2 className="h-8 w-8 text-primary" />
                                </motion.div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="content"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="space-y-6"
                            >
                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <MetricCard
                                        title="Products With Sales"
                                        value={summary?.totalProducts || 0}
                                        subtitle={`of ${summary?.totalProductsAll || 0} total products`}
                                        icon={Package}
                                        color="blue"
                                        index={0}
                                    />
                                    <MetricCard
                                        title="Total Qty Sold"
                                        value={numberWithCommas(summary?.totalQuantity || 0)}
                                        subtitle="Units sold in period"
                                        icon={ChartBar}
                                        color="purple"
                                        index={1}
                                    />
                                    <MetricCard
                                        title="Total Revenue"
                                        value={`${currency} ${numberWithCommas((summary?.totalRevenue || 0).toFixed(2))}`}
                                        subtitle="From all sold products"
                                        icon={CurrencyDollar}
                                        color="green"
                                        index={2}
                                    />
                                    <MetricCard
                                        title="Total Profit"
                                        value={`${currency} ${numberWithCommas((summary?.totalProfit || 0).toFixed(2))}`}
                                        subtitle="Revenue minus cost"
                                        icon={Trophy}
                                        color="orange"
                                        index={3}
                                    />
                                </div>

                                {/* Bar Chart - Top 10 (first page only) */}
                                {chartData.length > 0 && page === 1 && (
                                    <motion.div variants={itemVariants}>
                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="text-base flex items-center gap-2">
                                                    <Trophy className="h-4 w-4" weight="duotone" />
                                                    Top 10 Products
                                                </CardTitle>
                                                <CardDescription>
                                                    {sortBy === 'qty' ? 'Ranked by quantity sold' : 'Ranked by revenue generated'}
                                                </CardDescription>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="h-[400px]">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
                                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                            <XAxis type="number" tick={{ fontSize: 11 }} />
                                                            <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                                                            <Tooltip content={<CustomTooltip />} />
                                                            <Bar
                                                                dataKey={sortBy === 'qty' ? 'qty' : 'revenue'}
                                                                name={sortBy === 'qty' ? 'qty' : 'revenue'}
                                                                radius={[0, 4, 4, 0]}
                                                            >
                                                                {chartData.map((_, index) => (
                                                                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index] || '#0052CC'} />
                                                                ))}
                                                            </Bar>
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                )}

                                {/* Ranked Table */}
                                <motion.div variants={itemVariants}>
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className="text-base">All Products Ranking</CardTitle>
                                            <CardDescription>
                                                {pagination
                                                    ? `Showing ${((pagination.page - 1) * pagination.limit) + 1}–${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} products`
                                                    : 'Loading...'}
                                            </CardDescription>
                                        </CardHeader>
                                        <div className="p-0">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead className="w-16">#</TableHead>
                                                        <TableHead>Product Name</TableHead>
                                                        <TableHead className="text-right">Qty Sold</TableHead>
                                                        <TableHead className="text-right">Revenue</TableHead>
                                                        <TableHead className="text-right">Profit</TableHead>
                                                        <TableHead className="text-right">Margin</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {products.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={6} className="text-center h-32 text-muted-foreground">
                                                                No products found.
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        products.map((product) => (
                                                            <TableRow key={product.productId} className={product.totalQtySold === 0 ? "opacity-50" : ""}>
                                                                <TableCell className="font-bold">
                                                                    {product.rank <= 3 && product.totalQtySold > 0 ? (
                                                                        <Badge className={cn(
                                                                            "text-white",
                                                                            product.rank === 1 && "bg-yellow-500 hover:bg-yellow-600",
                                                                            product.rank === 2 && "bg-gray-400 hover:bg-gray-500",
                                                                            product.rank === 3 && "bg-amber-700 hover:bg-amber-800",
                                                                        )}>
                                                                            {product.rank}
                                                                        </Badge>
                                                                    ) : product.rank}
                                                                </TableCell>
                                                                <TableCell className="font-medium">{product.productName}</TableCell>
                                                                <TableCell className="text-right font-medium">
                                                                    {product.totalQtySold > 0 ? numberWithCommas(product.totalQtySold) : <span className="text-muted-foreground text-xs">No sales</span>}
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    {product.totalQtySold > 0 ? `${currency} ${numberWithCommas(product.totalRevenue.toFixed(2))}` : "—"}
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    {product.totalQtySold > 0 ? (
                                                                        <span className={product.totalProfit >= 0 ? "text-green-600" : "text-red-600"}>
                                                                            {currency} {numberWithCommas(product.totalProfit.toFixed(2))}
                                                                        </span>
                                                                    ) : "—"}
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    {product.totalQtySold > 0 ? (
                                                                        <span className={parseFloat(product.profitMargin) >= 0 ? "text-green-600" : "text-red-600"}>
                                                                            {product.profitMargin}%
                                                                        </span>
                                                                    ) : "—"}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        {/* Pagination */}
                                        {pagination && (
                                            <div className="flex items-center justify-between p-4 border-t">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-muted-foreground">Rows per page</span>
                                                    <Select value={limit.toString()} onValueChange={(val) => setLimit(parseInt(val))}>
                                                        <SelectTrigger className="w-[70px] h-8 text-xs">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="10">10</SelectItem>
                                                            <SelectItem value="20">20</SelectItem>
                                                            <SelectItem value="50">50</SelectItem>
                                                            <SelectItem value="100">100</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                                        disabled={page === 1 || isLoading}
                                                    >
                                                        Previous
                                                    </Button>
                                                    <span className="text-sm text-muted-foreground min-w-[100px] text-center">
                                                        Page {page} of {pagination.totalPages}
                                                    </span>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setPage(p => p + 1)}
                                                        disabled={page >= pagination.totalPages || isLoading}
                                                    >
                                                        Next
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </Card>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </>
            )}
        </div>
    );
}
